<template>
  <div class="rv" :class="`rv--${cfg.mode}`">
    <p v-if="!items.length" class="dw-empty">{{ $t("dashboardWidgets.report.empty") }}</p>

    <!-- Lista compacta: una columna como título y hasta cuatro como detalle. -->
    <ul v-else-if="cfg.mode === 'list'" class="rv-list">
      <li v-for="row in listRows" :key="row.id" class="rv-list__item">
        <router-link v-if="row.to && linkRows" :to="row.to" class="rv-list__title">{{ row.title }}</router-link>
        <span v-else class="rv-list__title">{{ row.title }}</span>
        <span v-if="row.meta.length" class="rv-list__meta">
          <span v-for="m in row.meta" :key="m.key" class="rv-list__meta-item" :title="m.label">
            <span class="rv-list__meta-label">{{ m.label }}</span> {{ m.value }}
          </span>
        </span>
      </li>
      <li v-if="total > listRows.length && fullReportPath && linkRows" class="rv-list__more">
        <router-link :to="fullReportPath">{{ $t("dashboardWidgets.report.seeAll", { n: total }) }}</router-link>
      </li>
    </ul>

    <!-- Total: cantidad de filas y la suma de las columnas elegidas. -->
    <div v-else-if="cfg.mode === 'kpi'" class="rv-kpi">
      <div class="rv-kpi__main">
        <span class="rv-kpi__value">{{ formatNumber(total) }}</span>
        <span class="rv-kpi__label">{{ $t("dashboardWidgets.report.rows") }}</span>
      </div>
      <div v-if="sums.length" class="rv-kpi__tiles">
        <div v-for="s in sums" :key="s.key" class="rv-kpi__tile">
          <span class="rv-kpi__tile-label">{{ s.label }}</span>
          <span class="rv-kpi__tile-value">{{ s.value }}</span>
        </div>
      </div>
      <p v-if="partial" class="rv-note">{{ $t("dashboardWidgets.report.partial", { n: items.length }) }}</p>
    </div>

    <!-- Gráfico -->
    <div v-else class="rv-chart">
      <div class="rv-chart__caption">{{ chartCaption }}</div>
      <div class="rv-chart__plot">
        <WidgetChart :type="cfg.chart.type" :points="points" />
      </div>
      <p v-if="partial" class="rv-note">{{ $t("dashboardWidgets.report.partial", { n: items.length }) }}</p>
    </div>
  </div>
</template>

<script>
/** Cómo se ve un reporte en el widget (y en la vista previa de su configuración). */
import { buildDynamicItemPrettySlug } from "@/utils/dynamicModelPaths";
import { formatNumberSync } from "@/utils/numberFormatter";
import { buildItemDisplayName } from "@/utils/currentItemLabel";
import WidgetChart from "./WidgetChart.vue";
import { getPathValue, displayValue, toNumber, aggregatePoints } from "../widgetData";

const LIST_LIMIT = 30;
const NOT_TITLE = ["number", "currency", "date", "boolean", "file", "group"];

export default {
  name: "ReportWidgetView",
  components: { WidgetChart },
  props: {
    cfg: { type: Object, required: true },
    items: { type: Array, default: () => [] },
    fields: { type: Array, default: () => [] },
    numericFields: { type: Array, default: () => [] },
    total: { type: Number, default: 0 },
    modelName: { type: String, default: "" },
    fullReportPath: { type: String, default: null },
    /** En la vista previa las filas no navegan. */
    linkRows: { type: Boolean, default: true },
  },
  computed: {
    locale() {
      return this.$i18n && this.$i18n.locale;
    },
    fieldByKey() {
      const map = {};
      this.fields.forEach((f) => {
        map[f.key] = f;
      });
      return map;
    },
    partial() {
      return this.cfg.mode !== "list" && this.total > this.items.length;
    },
    titleField() {
      const chosen = this.cfg.list && this.cfg.list.title && this.fieldByKey[this.cfg.list.title];
      return chosen || this.fields.find((f) => !NOT_TITLE.includes(f.type)) || this.fields[0] || null;
    },
    detailFields() {
      const chosen = ((this.cfg.list && this.cfg.list.details) || []).map((k) => this.fieldByKey[k]).filter(Boolean);
      if (chosen.length) return chosen;
      return this.fields.filter((f) => f !== this.titleField);
    },
    listRows() {
      const titleField = this.titleField;
      // Elegidas: hasta 4 con dato; automáticas: las 3 primeras con dato.
      const explicit = !!((this.cfg.list && this.cfg.list.details) || []).length;
      return this.items.slice(0, LIST_LIMIT).map((item, idx) => {
        const title =
          (titleField && displayValue(getPathValue(item, titleField.key), titleField, this.locale)) ||
          buildItemDisplayName(item) ||
          `#${idx + 1}`;
        const meta = this.detailFields
          .map((f) => ({ key: f.key, label: f.label, value: displayValue(getPathValue(item, f.key), f, this.locale) }))
          .filter((m) => m.value)
          .slice(0, explicit ? 4 : 3);
        const to = this.modelName && item._id ? `/i/${buildDynamicItemPrettySlug(this.modelName, item._id, title)}` : null;
        return { id: item._id || idx, title, meta, to };
      });
    },
    sums() {
      const chosen = ((this.cfg.kpi && this.cfg.kpi.fields) || []).map((k) => this.fieldByKey[k]).filter(Boolean);
      const list = chosen.length ? chosen : this.numericFields.slice(0, 3);
      return list.map((f) => ({
        key: f.key,
        label: f.label,
        value: formatNumberSync(this.items.reduce((sum, it) => sum + toNumber(getPathValue(it, f.key)), 0)),
      }));
    },
    xField() {
      return this.fieldByKey[this.cfg.chart.x] || null;
    },
    yField() {
      return this.fieldByKey[this.cfg.chart.y] || null;
    },
    points() {
      const c = this.cfg.chart;
      const isPie = c.type === "pie" || c.type === "donut";
      return aggregatePoints(this.items, {
        xField: this.xField,
        xDate: c.xDate,
        agg: this.yField ? c.agg : "count",
        yField: this.yField,
        limit: isPie ? Math.min(c.limit, 8) : c.limit,
        sort: c.sort,
        locale: this.locale,
        emptyLabel: this.$t("dashboardWidgets.report.noValue"),
        otherLabel: this.$t("dashboardWidgets.report.others"),
      });
    },
    chartCaption() {
      if (!this.xField) return "";
      const c = this.cfg.chart;
      const measure =
        c.agg === "count" || !this.yField
          ? this.$t("dashboardWidgets.settings.agg.count")
          : `${this.$t(`dashboardWidgets.settings.agg.${c.agg}`)} · ${this.yField.label}`;
      const by =
        this.xField.type === "date"
          ? `${this.xField.label} (${this.$t(`dashboardWidgets.settings.bucket.${c.xDate}`).toLowerCase()})`
          : this.xField.label;
      return this.$t("dashboardWidgets.report.chartCaption", { measure, by });
    },
  },
  methods: {
    formatNumber(n) {
      return formatNumberSync(n) || String(n || 0);
    },
  },
};
</script>

<style scoped>
.rv { height: 100%; display: flex; flex-direction: column; min-height: 0; }
.rv-list { list-style: none; margin: 0; padding: 0; }
.rv-list__item { display: flex; flex-direction: column; gap: 2px; padding: 7px 0; border-bottom: 1px solid var(--dw-border); }
.rv-list__item:last-child { border-bottom: 0; }
.rv-list__title { font-weight: 600; font-size: 0.875rem; color: var(--dw-text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
a.rv-list__title:hover { color: var(--bs-primary); }
.rv-list__meta { display: flex; flex-wrap: wrap; gap: 2px 12px; font-size: 0.78rem; color: var(--dw-muted); }
.rv-list__meta-item { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100%; }
.rv-list__meta-label { opacity: 0.75; }
.rv-list__meta-label::after { content: ":"; }
.rv-list__more { padding-top: 8px; font-size: 0.8rem; }
.rv-list__more a { color: var(--bs-primary); }
.rv-kpi { display: flex; flex-direction: column; gap: 12px; }
.rv-kpi__main { display: flex; align-items: baseline; gap: 8px; }
.rv-kpi__value { font-size: 2.2rem; font-weight: 700; line-height: 1; color: var(--bs-primary); }
.rv-kpi__label { color: var(--dw-muted); font-size: 0.85rem; }
.rv-kpi__tiles { display: grid; grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)); gap: 8px; }
.rv-kpi__tile { border: 1px solid var(--dw-border); border-radius: 8px; padding: 8px 10px; display: flex; flex-direction: column; gap: 2px; background: var(--dw-surface-2); }
.rv-kpi__tile-label { font-size: 0.75rem; color: var(--dw-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rv-kpi__tile-value { font-weight: 600; font-size: 1.05rem; }
.rv-chart { flex: 1; min-height: 0; display: flex; flex-direction: column; gap: 6px; }
.rv-chart__caption { font-size: 0.75rem; color: var(--dw-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rv-chart__plot { flex: 1; min-height: 0; }
.rv-note { margin: 4px 0 0; font-size: 0.72rem; color: var(--dw-muted); }
</style>
